import "./editableTable.css";

import { Form, Input, Table } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";

import Checkbox from "antd/lib/checkbox/Checkbox";
import RowActions, { ACTIONS_COLUMN } from "./RowActions";

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  type,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (editable && type === "checkbox") {
      form.setFieldsValue({
        [dataIndex]: record[dataIndex],
      });
    }
  }, [editable, type, form, record, dataIndex]);

  const toggleEdit = () => {
    if (type !== "checkbox") {
      setEditing(!editing);
      form.setFieldsValue({
        [dataIndex]: record[dataIndex],
      });
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : type === "checkbox" ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        valuePropName="checked"
      >
        <Checkbox onChange={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableTable = ({
  columns,
  dataSource,
  onDelete,
  onSave,
  ...restProps
}) => {
  const handleDelete = (key) => {
    onDelete(key);
  };

  const handleSave = (row) => {
    onSave(row);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  let modColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        type: col.type,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave: handleSave,
      }),
    };
  });

  modColumns.push({
    ...ACTIONS_COLUMN,
    dataIndex: "actions",
    render: (text, record) => (
      <RowActions onDelete={() => handleDelete(record.key)} />
    ),
  });

  return (
    <Table
      components={components}
      rowClassName={() => "editable-row"}
      bordered
      dataSource={dataSource}
      columns={modColumns}
      pagination={false}
      {...restProps}
    />
  );
};

export default EditableTable;
